import React, { useState } from "react";
import { base44 } from "../api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Building2, MapPin, Maximize2, DollarSign, Filter, Search, ArrowRight, Star } from "lucide-react";
import { motion } from "framer-motion";

export default function Projets() {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => { try { return await base44.entities.Project.filter({ visible_on_site: true }, '-created_date'); } catch { return []; } },
    retry: false
  });

  const availableCities = React.useMemo(() => {
    return [...new Set(projects.map(p => p.city).filter(Boolean))].sort();
  }, [projects]);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.city?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || project.type === typeFilter;
    const matchesStatus = statusFilter === "all" || project.status === statusFilter;
    const matchesCity = cityFilter === "all" || project.city === cityFilter;
    return matchesSearch && matchesType && matchesStatus && matchesCity;
  });

  const typeLabels = { terrain:"Terrain", maison:"Maison", lotissement:"Lotissement" };
  const statusLabels = { en_preparation:"En Préparation", en_vente:"En Vente", vendu:"Vendu", en_cours:"En Cours" };
  const statusColors = { en_preparation:"bg-gray-100 text-gray-800", en_vente:"bg-green-100 text-green-800", vendu:"bg-blue-100 text-blue-800", en_cours:"bg-yellow-100 text-yellow-800" };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-bg text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} className="text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Nos Projets Immobiliers</h1>
            <p className="text-xl text-gray-200">Terrains, maisons et lotissements de qualité</p>
          </motion.div>
        </div>
      </section>

      <section className="py-8 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input type="text" placeholder="Rechercher un projet, ville ou localisation..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black" />
            </div>
            <select value={cityFilter} onChange={e => setCityFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black">
              <option value="all">Toutes les villes</option>
              {availableCities.map(city => <option key={city} value={city}>{city}</option>)}
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black">
              <option value="all">Tous les types</option>
              <option value="terrain">Terrains</option>
              <option value="maison">Maisons</option>
              <option value="lotissement">Lotissements</option>
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black">
              <option value="all">Tous les statuts</option>
              <option value="en_vente">En Vente</option>
              <option value="en_preparation">En Préparation</option>
              <option value="en_cours">En Cours</option>
            </select>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="h-56 bg-gray-200"></div>
                  <div className="p-6"><div className="h-4 bg-gray-200 rounded mb-4"></div><div className="h-4 bg-gray-200 rounded w-2/3"></div></div>
                </div>
              ))}
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-xl text-gray-600">Aucun projet trouvé</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <motion.div key={project.id} initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }}>
                  <div className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all group h-full flex flex-col rounded-xl bg-white">
                    <div className="relative h-56 overflow-hidden bg-gradient-to-br from-black to-gray-800">
                      {project.featured_image ? (
                        <img src={project.featured_image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Building2 className="w-20 h-20 text-white/30" /></div>
                      )}
                      <div className="absolute top-4 left-4 flex gap-2">
                        <span className="bg-[#c41e3a] text-white px-3 py-1 rounded-full text-sm font-bold">{typeLabels[project.type]}</span>
                        {project.is_featured && <span className="bg-white text-black px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1"><Star className="w-3 h-3" />Vedette</span>}
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className={`${statusColors[project.status]} px-3 py-1 rounded-full text-sm font-medium`}>{statusLabels[project.status]}</span>
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-gray-600 mb-2"><MapPin className="w-4 h-4" /><span className="text-sm">{project.location}</span></div>
                      <h3 className="text-xl font-bold mb-2 text-black line-clamp-1">{project.title}</h3>
                      <p className="text-gray-600 mb-4 line-clamp-2 flex-1">{project.description}</p>
                      <div className="flex justify-between items-center mb-4 pb-4 border-b">
                        {project.area_m2 && <div><p className="text-sm text-gray-500">Surface</p><p className="font-bold text-black">{project.area_m2} m²</p></div>}
                        {project.price && <div><p className="text-sm text-gray-500">Prix</p><p className="font-bold text-[#c41e3a]">{project.price.toLocaleString()} FCFA</p></div>}
                      </div>
                      <Link to={`/projectdetails?id=${project.id}`}>
                        <button className="w-full gradient-bg text-white py-2 rounded-lg hover:opacity-90 flex items-center justify-center gap-2">
                          Voir les Détails<ArrowRight className="w-4 h-4" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
